import React from 'react'
import { IOtaReservation, IGuestEmail } from '../../static/interfaces'
import { agentService } from '../../core/services'
import {
  IOtaReservationEditPermissions,
  IOtaReservationMemoUpdateParams,
  IOtaReservationUpdateParams,
  ISaveResult,
} from './types'

interface IGuestEmailForm {
  id: number | null
  ota_reservation_id: number
  email: string
  created_at?: string
  updated_at?: string
}

interface IDraft {
  customerEmail: string
  customBookerName: string
  customGuestName: string
  memo: string
  internalMemo: string
  guestEmails: IGuestEmailForm[]
}

export interface IOtaReservationGuestEditor extends IDraft {
  guestEmailsEditable: boolean
  customNamesEditable: boolean
  memoEditable: boolean
  setCustomerEmail: (value: string) => void
  customerDetailsEditable: boolean
  setCustomBookerName: (value: string) => void
  setCustomGuestName: (value: string) => void
  setMemo: (value: string) => void
  setInternalMemo: (value: string) => void
  onChangeGuestEmail: (index: number, value: string) => void
}

const getFormattedGuestEmails = (
  guestEmails: IGuestEmail[],
  otaReservationId: number
): IGuestEmailForm[] => {
  return Array.from({ length: 3 }).map((_, index) =>
    index >= guestEmails.length
      ? { id: null, ota_reservation_id: otaReservationId, email: '' }
      : guestEmails[index]
  )
}

const createGuestDraft = (
  reservation: IOtaReservation,
  guestEmails: IGuestEmailForm[]
): IDraft => ({
  customerEmail: reservation.customer_email ?? '',
  customBookerName: reservation.custom_booker_name ?? '',
  customGuestName: reservation.custom_guest_name ?? '',
  memo: reservation.memo ?? '',
  internalMemo: reservation.internal_memo ?? '',
  guestEmails,
})

const emptyToNull = (value: string): string | null => value || null

export const useGuestEditor = (
  otaReservation: IOtaReservation,
  initialEmails: IGuestEmail[],
  permissions: IOtaReservationEditPermissions
) => {
  const [savedGuestEmails, setSavedGuestEmails] = React.useState(() =>
    getFormattedGuestEmails(initialEmails, otaReservation.id)
  )
  const [draft, setDraft] = React.useState<IDraft>(() =>
    createGuestDraft(otaReservation, savedGuestEmails)
  )

  const { canUpdateOtaReservation, canUpdateMemo } = permissions
  const { is_past, post } = otaReservation
  const editableCurrently = canUpdateOtaReservation && !is_past

  const fieldPermissions = {
    customNamesEditable: canUpdateOtaReservation,
    customerDetailsEditable: editableCurrently,
    guestEmailsEditable: editableCurrently && post?.post_type === 'private',
    memoEditable: canUpdateMemo && !is_past,
  }

  const updateDraft = (updates: Partial<typeof draft>) =>
    setDraft(current => ({ ...current, ...updates }))

  const onChangeGuestEmail = React.useCallback(
    (index: number, value: string) => {
      setDraft(currentDraft => ({
        ...currentDraft,
        guestEmails: currentDraft.guestEmails.map((guestEmail, currentIndex) =>
          currentIndex === index
            ? {
                ...guestEmail,
                ota_reservation_id: Number(otaReservation.id),
                email: value,
              }
            : guestEmail
        ),
      }))
    },
    [otaReservation.id]
  )

  const buildUpdateParams = (): IOtaReservationUpdateParams => {
    const params: IOtaReservationUpdateParams = {}
    if (fieldPermissions.customNamesEditable) {
      if (draft.customBookerName !== (otaReservation.custom_booker_name ?? '')) {
        params.custom_booker_name = emptyToNull(draft.customBookerName)
      }
      if (draft.customGuestName !== (otaReservation.custom_guest_name ?? '')) {
        params.custom_guest_name = emptyToNull(draft.customGuestName)
      }
    }
    if (
      fieldPermissions.customerDetailsEditable &&
      draft.customerEmail !== (otaReservation.customer_email ?? '')
    ) {
      params.customer_email = emptyToNull(draft.customerEmail)
    }
    return params
  }

  const buildMemoUpdateParams = (): IOtaReservationMemoUpdateParams => {
    const params: IOtaReservationMemoUpdateParams = {}
    if (!fieldPermissions.memoEditable) {
      return params
    }

    if (draft.memo !== (otaReservation.memo ?? '')) {
      params.memo = emptyToNull(draft.memo)
    }
    if (draft.internalMemo !== (otaReservation.internal_memo ?? '')) {
      params.internal_memo = emptyToNull(draft.internalMemo)
    }
    return params
  }

  /**
   * 編集可能時のみ保存処理を実行
   * @returns 保存後のゲストメール情報と、更新APIを実行したかどうか
   */
  const guestEmailsChanged = draft.guestEmails.some((guestEmail, index) => {
    const savedGuestEmail = savedGuestEmails[index]
    return (
      !savedGuestEmail ||
      guestEmail.id !== savedGuestEmail.id ||
      guestEmail.email !== savedGuestEmail.email
    )
  })

  const saveGuestEmails = async (): Promise<ISaveResult<IGuestEmailForm[]>> => {
    if (!fieldPermissions.guestEmailsEditable || !guestEmailsChanged) {
      return { value: savedGuestEmails, saved: false }
    }
    const response = await agentService.updateGuestEmails(otaReservation.id, draft.guestEmails)
    return {
      value: getFormattedGuestEmails(
        response.updatedGuestEmails as IGuestEmail[],
        otaReservation.id
      ),
      saved: true,
    }
  }

  const reset = (reservation = otaReservation, emails = savedGuestEmails) => {
    setSavedGuestEmails(emails)
    setDraft(createGuestDraft(reservation, emails))
  }

  const guestEdit: IOtaReservationGuestEditor = {
    ...fieldPermissions,
    ...draft,
    setCustomerEmail: customerEmail => updateDraft({ customerEmail }),
    setCustomBookerName: customBookerName => updateDraft({ customBookerName }),
    setCustomGuestName: customGuestName => updateDraft({ customGuestName }),
    setMemo: memo => updateDraft({ memo }),
    setInternalMemo: internalMemo => updateDraft({ internalMemo }),
    onChangeGuestEmail,
  }

  return { editor: guestEdit, buildUpdateParams, buildMemoUpdateParams, saveGuestEmails, reset }
}
